import { DataGridRef } from '@cimplico/components';
import { useMemo, useRef, useState } from 'react';
import { Entity } from '../../types/entity';
import { useParams } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import SecondaryPageActions from '../../components/secondary-page-actions/SecondaryPageActions';
import EntitiesTable from '../entities/EntitiesTable';
import EntitiesPrimaryActions from './EntitiesPrimaryActions';
import RemoveFromGroup from '../entities/RemoveFromGroup';
import EntitiesArchiveConfirm from '../entities/EntitiesArchiveConfirm';
import EntitiesUnarchiveConfirm from '../entities/EntitiesUnarchiveConfirm';

type EntitiesSectionProps = {
    groupName: string;
    fyiGroupName?: string;
    fyiGroupId?: number;
};

function EntitiesSection({ groupName, fyiGroupName, fyiGroupId }: EntitiesSectionProps): JSX.Element {
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('');
    const [filter, setFilter] = useState('');
    const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
    const [secondaryAction, setSecondaryAction] = useState<string | undefined>();
    const { shortId } = useFirmContext();
    const activeSubscription = useIsActiveSubscription();
    const { isExternalUser } = useIsFirmRole();

    const tableRef = useRef<DataGridRef | null>(null);
    const { id: groupId } = useParams();

    function clearAction(): void {
        setSecondaryAction(undefined);
    }

    function resetTable(): void {
        clearAction();
        setSelectedEntities([]);
        tableRef?.current?.clear();
        tableRef?.current?.resetPagination();
    }

    const canArchiveEntities = useMemo(
        () => selectedEntities.filter((e) => e.activeWorkpapers.length === 0 && !e.isArchived),
        [selectedEntities]
    );
    const canUnarchiveEntities = useMemo(() => selectedEntities.filter((e) => e.isArchived), [selectedEntities]);

    return (
        <>
            <RemoveFromGroup
                entities={selectedEntities}
                isOpen={secondaryAction === 'REMOVE'}
                onClose={clearAction}
                onRemove={resetTable}
                title="Remove Entities"
                description={`Do you want to remove the selected entities from ${groupName}?`}
            />
            <EntitiesArchiveConfirm
                entities={canArchiveEntities}
                isOpen={secondaryAction === 'ARCHIVE'}
                onClose={clearAction}
                onArchive={resetTable}
            />
            <EntitiesUnarchiveConfirm
                entities={canUnarchiveEntities}
                isOpen={secondaryAction === 'UNARCHIVE'}
                onClose={clearAction}
                onUnarchive={resetTable}
            />
            <div className="flex justify-end items-center pb-2">
                {selectedEntities.length > 0 ? (
                    <SecondaryPageActions>
                        <SecondaryPageActions.Button
                            label={`Remove from Group (${selectedEntities.length})`}
                            onClick={(): void => setSecondaryAction('REMOVE')}
                            disabled={!activeSubscription || isExternalUser}
                        />
                        {canUnarchiveEntities.length > 0 && (
                            <SecondaryPageActions.Button
                                label={`Unarchive (${canUnarchiveEntities.length})`}
                                disabled={!activeSubscription || isExternalUser}
                                onClick={(): void => setSecondaryAction('UNARCHIVE')}
                            />
                        )}
                        {canArchiveEntities.length > 0 && (
                            <SecondaryPageActions.Button
                                type="danger"
                                label={`Archive (${canArchiveEntities.length})`}
                                disabled={!activeSubscription || isExternalUser}
                                onClick={(): void => setSecondaryAction('ARCHIVE')}
                            />
                        )}
                    </SecondaryPageActions>
                ) : (
                    <EntitiesPrimaryActions
                        onFilterChange={setFilter}
                        onSearchChange={setSearch}
                        onSortChange={setSort}
                        fyiGroupId={fyiGroupId}
                        fyiGroupName={fyiGroupName}
                    />
                )}
            </div>
            <EntitiesTable
                search={search}
                sort={sort}
                filter={`${filter}&group=${groupId}`}
                onSelect={setSelectedEntities}
                tableRef={tableRef}
                emptyStateNavigateTo={`/entities/new?groupId=${groupId}&returnTo=/${shortId}/groups/${groupId}?tab=entities`}
            />
        </>
    );
}

export default EntitiesSection;
